import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Divider,
  Avatar,
  Modal,
  Input,
  message,
} from "antd";
import { UserOutlined, CloseOutlined, SwapOutlined } from "@ant-design/icons";
import ErrorLogout from "../../../helpers/ErrorLogout";
import Pagination from "../Pagination";
import { State } from "country-state-city";
import LocationDropDown from "./LocationDropDown";
import TableSkeleton from "./TableSkeleton";
import moment from "moment";
import {
  assignSurvey,
  getSingleSurvey,
  getSurveyorToAssign,
  reassignSurvey,
} from "../../../api/CommonRequest";
import { useNavigate } from "react-router-dom";

const AssignSurvey = ({ surveyID }) => {
  const navigate = useNavigate();
  const [surveyorLoader, setSurveyorLoader] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(5);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [loader, setLoader] = useState(false);
  const [surveyors, setSurveyors] = useState([]);
  const [surveyDetails, setSurveyDetails] = useState([]);
  const [selectedSurveyor, setSelectedSurveyor] = useState(null);

  const [instructionModalVisible, setInstructionModalVisible] = useState(false);
  const [instruction, setInstruction] = useState("");

  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState({
    state: null,
    district: null,
    city: null,
  });
  const [triggerSuccess, setTriggerSuccess] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);

  // Get Indian states, districts, and cities
  const indiaCode = "IN";

  // ------------------------ GET THE SURVEY DETAILS--------------

  useEffect(() => {
    const GetSurvey = async () => {
      try {
        setLoader(true);
        const { data } = await getSingleSurvey(surveyID);
        console.log(data, "data");

        if (data?.success) {
          setSurveyDetails(data.singleSurvey);
          if (data.singleSurvey?.assignTo.length > 0) {
            handleExitSurveyor(
              data.singleSurvey.assignTo[0]?.surveyorId,
              data.singleSurvey.assignTo[0]?.instructions
            );
          }
          const newSelectedLocations = {
            state: null,
            district: null,
            city: null,
          };

          if (data.singleSurvey.state) {
            const stateObj = State.getStatesOfCountry(indiaCode).find(
              (state) => state.name === data.singleSurvey.state
            );

            if (stateObj) {
              setSelectedState(stateObj.isoCode);
              newSelectedLocations.state = stateObj.name;
            }
          }

          if (data.singleSurvey.district) {
            setSelectedDistrict(data.singleSurvey.district);
            newSelectedLocations.district = data.singleSurvey.district;
          }

          if (data.singleSurvey.city) {
            setSelectedCity(data.singleSurvey.city);
            newSelectedLocations.city = data.singleSurvey.city;
          }

          setSelectedLocations(newSelectedLocations);
          setTriggerSuccess(true);
          setLoader(false);
        }
      } catch (error) {
        setLoader(false);

        console.log(error);
        if (error?.response?.status === 401) {
          ErrorLogout(error);
        }
      }
    };
    GetSurvey();
  }, [surveyID]);

  // ------------------- ALL THE SURVEYORS LIST---------------------
  useEffect(() => {
    const GetSurveyor = async () => {
      try {
        setSurveyorLoader(true);
        const { data } = await getSurveyorToAssign({
          state: selectedState
            ? State.getStateByCodeAndCountry(selectedState, indiaCode)?.name
            : "",
          district: selectedDistrict || "",
          city: selectedCity || "",
          page: currentPage,
          limit: limit,
        });
        console.log(data);

        if (data?.success) {
          setSurveyors(data?.surveyorList);
          setTotalPages(data?.totalPages);
          setCurrentPage(data?.currentPage);
          setTotalItems(data?.totalItems);
          setSurveyorLoader(false);
        }
      } catch (error) {
        setSurveyorLoader(false);
        console.log(error);
        if (error?.response?.status === 401) {
          ErrorLogout(error);
        }
      }
    };
    if (triggerSuccess) {
      GetSurveyor();
    }
  }, [
    limit,
    currentPage,
    selectedState,
    selectedDistrict,
    selectedCity,
    triggerSuccess,
  ]);

  const removeLocation = (locationType) => {
    setSelectedLocations((prev) => ({
      ...prev,
      [locationType]: null,
    }));

    if (locationType === "state") {
      setSelectedState(null);
      // setSelectedDistrict(null);
      // setSelectedCity(null);
    } else if (locationType === "district") {
      setSelectedDistrict(null);
      // setSelectedCity(null);
    } else if (locationType === "city") {
      setSelectedCity(null);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleLimit = (e) => {
    const selectedValue = parseInt(e.target.value);
    setLimit(selectedValue);
    setCurrentPage(1);
  };

  const handleExitSurveyor = (surveyor, instructions) => {
    // For reassign page, replace the existing selection
    setSelectedSurveyor({ surveyor, instructions });
  };
  const handleSelectSurveyor = (surveyor) => {
    // For reassign page, replace the existing selection
    setSelectedSurveyor({ surveyor, instructions: "" });
  };

  const handleRemoveSurveyor = () => {
    setSelectedSurveyor(null);
  };
  const handleDiscard = () => {
    setSelectedSurveyor(null);
  };

  const openInstructionModal = () => {
    setInstructionModalVisible(true);
  };

  const handleInstructionSubmit = () => {
    setSelectedSurveyor((prevState) => ({
      ...prevState,
      instructions: instruction,
    }));

    setInstructionModalVisible(false);
  };

  const handleAssignSurveyors = () => {
    Modal.confirm({
      title: "Confirm Assignment",
      content: `Are you sure you want to assign ${selectedSurveyor?.surveyor?.name} to this survey?`,
      onOk: async () => {
        try {
          setAssignLoading(true);

          const { data } = await assignSurvey(
            surveyDetails._id,
            selectedSurveyor
          );
          if (data.success) {
            message.success("Surveyors assigned successfully");
            navigate("/survey-list");
          } else {
            message.error("Failed to assign surveyors");
          }
        } catch (error) {
          console.error("Error assigning surveyors:", error);
          message.error("An error occurred while assigning surveyors");
        } finally {
          setAssignLoading(false);
        }
      },
      onCancel() {
        // Optionally, you can add a message or action when the user cancels
        console.log("Assignment cancelled");
      },
    });
  };
  // -------------- REASSIGN--------
  const handleReassignSurveyor = () => {
    if (selectedSurveyor.length !== 1) {
      message.error("Please select  surveyor for reassignment");
      return;
    }

    Modal.confirm({
      title: "Confirm Reassignment",
      content: `Are you sure you want to reassign this survey to ${selectedSurveyor.surveyor.name}?`,
      onOk: async () => {
        try {
          setAssignLoading(true);

          const { data } = await reassignSurvey(
            surveyDetails._id,
            selectedSurveyor
          );
          if (data.success) {
            message.success("Surveyor reassigned successfully");

            // You might want to refresh the survey details or update local state here
          } else {
            message.error("Failed to reassign surveyor");
          }
        } catch (error) {
          console.error("Error reassigning surveyor:", error);
          message.error("An error occurred while reassigning the surveyor");
        } finally {
          setAssignLoading(false);
        }
      },
      onCancel() {
        console.log("Reassignment cancelled");
      },
    });
  };
  // ------------ TABLE--------
  const columns = [
    {
      title: "Surveyor",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Avatar
            icon={<UserOutlined />}
            src={record.profileImage} // Add this field to your data if you have avatar URLs
            style={{ marginRight: "8px" }}
          />
          {text}
        </div>
      ),
    },
    // { title: "Category", dataIndex: "category", key: "category" },
    {
      title: "State",
      dataIndex: "roleDetails",
      key: "state",
      render: (data) => <p>{data.state}</p>,
    },
    {
      title: "District",
      dataIndex: "roleDetails",
      key: "district",
      render: (data) => <p>{data.district}</p>,
    },
    {
      title: "City",
      dataIndex: "roleDetails",
      key: "city",
      render: (data) => <p>{data.city}</p>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={status === "active" ? "green" : "red"}
          className="rounded-full"
        >
          {status === "active" ? "● Available" : "○ Unavailable"}
        </Tag>
      ),
    },
    { title: "Total Surveys", dataIndex: "totalSurveys", key: "totalSurveys" },

    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        return (
          <>
            {selectedSurveyor?.surveyor?._id !== record._id ? (
              <Button
                type="primary"
                className="px-8"
                onClick={() => handleSelectSurveyor(record)}
                disabled={selectedSurveyor}
              >
                Select
              </Button>
            ) : (
              <Button
                type="primary"
                className="px-6"
                onClick={() => handleRemoveSurveyor()}
              >
                Remove
              </Button>
            )}
          </>
        );
      },
    },
  ];

  return (
    <div className="flex-grow overflow-y-auto scrollbar-hide p-4 lg:p-6 md:p-5">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="text-left w-full">
            <h1 className="text-2xl font-bold text-gray-800">Assign Surveyor</h1>
            <p className="text-sm text-gray-500 mt-1">Assign technicians to the survey task below.</p>
          </div>
          <Button
            onClick={() => navigate("/survey-list")}
            className="h-10 px-6 rounded-lg font-medium hover:bg-gray-50 transition-all border-gray-200 text-gray-600 flex items-center gap-2"
          >
            Back to List
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Column: Survey Details & Filters */}
          <div className="w-full lg:w-[450px] space-y-6">
            <Card className="rounded-2xl border-gray-100 shadow-sm">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-2 h-6 bg-[#1955EB] rounded-full"></span>
                Survey Details
              </h2>
              <div className="bg-gray-50/50 p-4 rounded-xl space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 font-medium">Survey ID</span>
                  <span className="text-gray-800 font-bold bg-white px-3 py-1 rounded-lg border border-gray-100 shadow-sm">
                    #{surveyDetails?._id?.slice(-8).toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 font-medium">Client</span>
                  <span className="text-gray-800 font-semibold">{surveyDetails?.clientName}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 font-medium">Created Date</span>
                  <span className="text-gray-800">
                    {surveyDetails?.createdAt ? moment(surveyDetails.createdAt).format("DD MMM YYYY") : "N/A"}
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Filter by Location</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {Object.entries(selectedLocations).map(([key, value]) => (
                    value && (
                      <Tag
                        key={key}
                        closable
                        onClose={() => removeLocation(key)}
                        className="bg-blue-50 text-blue-600 border-blue-100 px-3 py-1 rounded-lg font-medium"
                      >
                        {value}
                      </Tag>
                    )
                  ))}
                </div>
                <LocationDropDown
                  setSelectedState={setSelectedState}
                  setSelectedDistrict={setSelectedDistrict}
                  setSelectedCity={setSelectedCity}
                  selectedState={selectedState}
                  selectedDistrict={selectedDistrict}
                  setSelectedLocations={setSelectedLocations}
                  selectedCity={selectedCity}
                />
              </div>
            </Card>

            {/* Selection Summary Card - Visible when surveyor selected */}
            <Card className={`rounded-2xl border-gray-100 shadow-sm transition-all duration-300 ${selectedSurveyor ? 'border-[#1955EB] bg-blue-50/30' : 'bg-gray-50/30 opacity-60'}`}>
              <h2 className="text-lg font-bold text-gray-800 mb-2">Assignment Summary</h2>
              <p className="text-sm text-gray-500 mb-4">Select a technician from the list to assign.</p>

              {selectedSurveyor ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-blue-100 shadow-sm relative group">
                    <Avatar
                      icon={<UserOutlined />}
                      src={selectedSurveyor?.surveyor?.profileImage}
                      className="bg-blue-100 text-[#1955EB]"
                    />
                    <div className="flex-grow min-w-0">
                      <p className="text-sm font-bold text-gray-800 truncate">{selectedSurveyor?.surveyor?.name}</p>
                      <button
                        onClick={() => openInstructionModal()}
                        className="text-xs text-[#1955EB] font-bold hover:underline"
                      >
                        {selectedSurveyor.instructions ? "✏️ Edit Instructions" : "+ Add Instructions"}
                      </button>
                    </div>
                    <button
                      onClick={handleRemoveSurveyor}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <CloseOutlined size={12} />
                    </button>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      className="flex-1 h-10 rounded-xl font-bold border-gray-200 text-gray-600"
                      onClick={handleDiscard}
                    >
                      Discard
                    </Button>
                    <Button
                      type="primary"
                      className="flex-1 h-10 rounded-xl font-bold bg-[#1955EB] hover:bg-blue-700 shadow-lg shadow-blue-200"
                      onClick={handleAssignSurveyors}
                      loading={assignLoading}
                    >
                      {surveyDetails?.assignTo?.length > 0 ? "Re-Assign" : "Confirm"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="h-32 flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-200 rounded-2xl">
                  <UserOutlined className="text-3xl mb-2" />
                  <p className="text-xs font-medium uppercase tracking-tighter">No Technician Selected</p>
                </div>
              )}
            </Card>
          </div>

          {/* Right Column: Surveyor List */}
          <div className="flex-grow">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-50 bg-gray-50/30 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-800">Technicians List</h2>
                <span className="text-xs font-bold text-[#1955EB] bg-blue-50 px-3 py-1 rounded-full uppercase">
                  {totalItems} Available
                </span>
              </div>

              {surveyorLoader ? (
                <div className="p-12"><TableSkeleton /></div>
              ) : (
                <div className="p-2">
                  <Table
                    columns={columns}
                    dataSource={surveyors}
                    className="custom-table-header w-full overflow-x-auto custom-pagination"
                    pagination={false}
                    rowKey="_id"
                    size="middle"
                  />
                  <div className="p-4 border-t border-gray-50">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      limit={limit}
                      totalItem={totalItems}
                      onPageChange={handlePageChange}
                      handleLimit={handleLimit}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <Modal
          title={<span className="text-lg font-bold text-gray-800">Assignment Instructions</span>}
          open={instructionModalVisible}
          onOk={handleInstructionSubmit}
          onCancel={() => setInstructionModalVisible(false)}
          className="rounded-2xl overflow-hidden"
          okText="Save Instructions"
          okButtonProps={{ className: "bg-[#1955EB] rounded-lg h-10 font-bold" }}
          cancelButtonProps={{ className: "rounded-lg h-10" }}
        >
          <div className="py-4">
            <p className="text-sm text-gray-500 mb-3">Add any specific notes or instructions for the technician performing this survey.</p>
            <Input.TextArea
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="e.g. Check for water leakage in the basement, verify serial numbers on all units..."
              rows={5}
              className="rounded-xl border-gray-200 focus:border-[#1955EB] transition-all p-3"
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default AssignSurvey;
